import { useEffect, useRef } from "react";
import { BrowserRouter, useRoutes, useLocation, useNavigate } from "react-router-dom";
import { routes } from "./routes";
import { ThemeProvider } from "./context/ThemeContext";
import { useAuth } from "react-oidc-context";
import { UserProvider } from "./context/UserContext";
import ErrorBoundary from './components/common/ErrorBoundary';
import SupportFloatingButton from './components/common/SupportFloatingButton';
import FullPageLoader from './components/common/FullPageLoader';
import { trackPageView } from './utils/metaPixel';
import { retryLinkedAccountSignin, getSafeReturnPath } from './services/socialLogin';
import { userManager } from './services/authManager';
import { isNativeApp, listenAppUrls } from './services/nativeApp';

// Rotas públicas/onboarding sem acesso ao menu Perfil: exibem o botão flutuante de suporte.
const SUPPORT_BUTTON_PATHS = [
  "/login",
  "/store-signup",
  "/store-registration",
  "/phone-registration",
  "/phone-verification",
];

// Routes renderer component
const AppRoutes = () => {
  const element = useRoutes(routes);
  const auth = useAuth();
  const location = useLocation();
  const showSupportButton = SUPPORT_BUTTON_PATHS.includes(location.pathname);
  // Evita contar o PageView inicial em duplicidade: o snippet base do Meta Pixel
  // (public/index.html) já dispara um PageView na primeira carga da página.
  const isFirstRouteRef = useRef(true);

  // App é uma SPA (react-router sem reload de página): o Meta Pixel só rastreia
  // a navegação automaticamente na primeira carga, então disparamos manualmente
  // a cada troca de rota.
  useEffect(() => {
    if (isFirstRouteRef.current) {
      isFirstRouteRef.current = false;
      return;
    }
    trackPageView();
  }, [location.pathname]);

  // Primeiro login após vincular um novo provider a uma conta existente
  // (Lambda PreSignUp) volta com erro do Cognito — refaz o login uma vez.
  useEffect(() => {
    if (auth.error) {
      retryLinkedAccountSignin(auth, auth.error);
    }
  }, [auth, auth.error]);

  // App iOS/Android: retorno do login pelo custom scheme e Universal/App Links.
  // Registrado uma única vez: o navigate do react-router muda a cada rota, e
  // re-registrar reprocessava a URL de abertura (callback já consumido → loop /login ↔ /).
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const authRef = useRef(auth);
  authRef.current = auth;
  useEffect(() => {
    if (!isNativeApp) return undefined;
    return listenAppUrls({
      navigate: (to) => navigateRef.current(to),
      onSigninCallback: async (url) => {
        try {
          const user = await userManager.signinCallback(url);
          navigateRef.current(getSafeReturnPath(user?.state?.returnTo) || "/", { replace: true });
        } catch (err) {
          if (!retryLinkedAccountSignin(authRef.current, err)) {
            console.error("Native signin callback error:", err);
            navigateRef.current("/login", { replace: true });
          }
        }
      },
    });
  }, []);

  return (
    <>
      {element}
      {showSupportButton && <SupportFloatingButton />}
    </>
  );
};

function App() {
  const auth = useAuth();

  // Boot do OIDC (leitura da sessão / callback do login): o router só monta
  // depois, já com a URL final (returnTo restaurado em onSigninCallback).
  if (auth.isLoading && !auth.activeNavigator) {
    return <FullPageLoader />;
  }

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeProvider>
          <UserProvider>
            <AppRoutes />
          </UserProvider>
        </ThemeProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
